import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CalendarDays, ExternalLink, Info } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import SeatGauge from "@/components/site/SeatGauge";
import VideoLightbox from "@/components/site/VideoLightbox";
import { IMG, SAMPLE_VIDEO } from "@/components/site/content";
import { useLang } from "@/context/LanguageContext";
import logo from "@/assets/szn-logo.webp";

const POSTERS = [IMG.scrap2, IMG.heroVibe, IMG.scrap1];

export default function Booking({ config, onCta }) {
  const { t } = useLang();
  const pr = t.price;
  const fc = t.finalCta;
  const isWaitlist = config.phase !== "booking";

  return (
    <>
      {/* Video testimonials — right before price/CTA */}
      <section className="bg-[#14283A] py-20 text-[#F6F1E8]" data-testid="section-temoignages">
        <div className="mx-auto max-w-7xl px-5">
          <div className="mb-12 text-center">
            <span className="font-marker text-lg text-[#EA7A4F]">{t.testimonials.kicker}</span>
            <Reveal>
              <h2 className="mt-2 font-display text-3xl sm:text-5xl">{t.testimonials.title}</h2>
            </Reveal>
          </div>
          <div className="mx-auto grid max-w-3xl grid-cols-2 gap-8 sm:grid-cols-3">
            {t.testimonials.items.map(([caption, subtitle], i) => (
              <div key={i} className={i === 2 ? "col-span-2 mx-auto w-1/2 sm:col-span-1 sm:w-full" : ""}>
                <VideoLightbox
                  poster={POSTERS[i]}
                  src={SAMPLE_VIDEO}
                  caption={caption}
                  subtitle={subtitle}
                  rotate={i % 2 === 0 ? "-3deg" : "3deg"}
                  testId={`testimonial-video-${i}`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10 + 11. Prix, jauge & dates */}
      <section id="prix" className="bg-[#F6F1E8] py-24 sm:py-32" data-testid="section-prix">
        <div className="mx-auto max-w-5xl px-5">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_0.9fr]">
            <Reveal>
              <div className="relative h-full overflow-hidden rounded-2xl border-2 border-[#14283A] bg-[#14283A] p-8 text-[#F6F1E8] shadow-[8px_8px_0_0_#EA7A4F] sm:p-10">
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-[#3F9BA6]">
                  <CalendarDays className="h-4 w-4" /> {pr.dates}
                </div>
                <div className="mt-6">
                  <span className="font-marker text-lg text-[#EA7A4F]">
                    {isWaitlist ? pr.fromLabel : pr.exactLabel}
                  </span>
                  <div className="mt-1 font-display text-5xl sm:text-6xl" data-testid="price-value">
                    {config.prix_affiche}
                  </div>
                </div>

                {isWaitlist ? (
                  <div className="mt-4 flex items-start gap-2 rounded-lg border border-[#EA7A4F]/40 bg-[#EA7A4F]/10 p-3 text-sm text-[#F6F1E8]/85" data-testid="price-note">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#EA7A4F]" />
                    <span>{config.prix_note || pr.noteFallback}</span>
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-[#F6F1E8]/70">{pr.bookingSub}</p>
                )}

                <div className="mt-8">
                  {isWaitlist ? (
                    <button
                      onClick={onCta}
                      data-testid="price-cta-waitlist"
                      className="grad-bar w-full rounded-full py-4 text-base font-bold text-white transition-transform hover:scale-[1.02] active:scale-95"
                    >
                      {pr.ctaWaitlist}
                    </button>
                  ) : (
                    <a
                      href={config.booking_url || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="price-cta-booking"
                      className="grad-bar flex w-full items-center justify-center gap-2 rounded-full py-4 text-base font-bold text-white transition-transform hover:scale-[1.02] active:scale-95"
                    >
                      {pr.ctaBooking} <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                  <p className="mt-3 text-center text-xs text-[#F6F1E8]/60">
                    {isWaitlist ? pr.disclaimerWaitlist : pr.disclaimerBooking}
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="flex h-full flex-col justify-between rounded-2xl border-2 border-[#14283A] bg-white p-8">
                <div>
                  <h3 className="font-display text-2xl text-[#14283A]">{pr.gaugeTitle}</h3>
                  <p className="mt-2 text-sm text-[#14283A]/70">{pr.gaugeSub}</p>
                  <div className="mt-6">
                    <SeatGauge total={config.places_total} restantes={config.places_restantes} />
                  </div>
                  <p className="mt-4 rounded-lg bg-[#3F9BA6]/10 p-3 text-sm text-[#14283A]/80" data-testid="gauge-note">
                    {pr.gaugeNote}
                  </p>
                </div>
                <div className="mt-6 rounded-xl border-2 border-dashed border-[#14283A]/25 p-4">
                  <div className="flex items-center gap-2 text-[#14283A]">
                    <CalendarDays className="h-5 w-5 text-[#EA7A4F]" />
                    <span className="font-bold">{pr.dateCard}</span>
                  </div>
                  <p className="mt-1 text-sm text-[#14283A]/65">{pr.dateCardSub}</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 12. FAQ */}
      <section id="faq" className="bg-[#F6F1E8] py-24 sm:py-32" data-testid="section-faq">
        <div className="mx-auto max-w-3xl px-5">
          <div className="mb-12 text-center">
            <span className="font-marker text-lg text-[#3F9BA6]">{t.faq.kicker}</span>
            <Reveal>
              <h2 className="mt-2 font-display text-4xl text-[#14283A] sm:text-6xl">{t.faq.title}</h2>
            </Reveal>
          </div>
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {t.faq.items.map(([q, a], i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="overflow-hidden rounded-xl border-2 border-[#14283A] bg-white px-5"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="py-5 text-left font-display text-base text-[#14283A] hover:no-underline sm:text-lg">
                  {q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-[#14283A]/75">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 13. CTA final */}
      <section className="relative overflow-hidden bg-[#14283A] py-28 text-center text-[#F6F1E8]" data-testid="section-cta-final">
        <div className="pointer-events-none absolute -left-20 top-0 h-72 w-72 rounded-full bg-[#EA7A4F]/20 blur-3xl" />
        <div className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-[#3F9BA6]/20 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-3xl px-5">
          <Reveal>
            <img
              src={logo}
              alt="SZN Seazoner Lifestyle"
              className="mx-auto w-56 rounded-2xl bg-white p-4 shadow-xl sm:w-72"
              data-testid="final-cta-logo"
            />
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-8 font-display text-4xl leading-[0.95] sm:text-6xl">
              <span className="grad-text">{fc.signature}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mx-auto mt-5 max-w-lg text-lg text-[#F6F1E8]/80">{fc.body}</p>
          </Reveal>
          <Reveal delay={0.22}>
            {isWaitlist ? (
              <button
                onClick={onCta}
                data-testid="final-cta-button"
                className="grad-bar mt-9 rounded-full px-10 py-4 text-lg font-bold text-white shadow-lg transition-transform hover:scale-105 active:scale-95"
              >
                {fc.ctaWaitlist}
              </button>
            ) : (
              <a
                href={config.booking_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="final-cta-button"
                className="grad-bar mt-9 inline-flex items-center gap-2 rounded-full px-10 py-4 text-lg font-bold text-white shadow-lg transition-transform hover:scale-105 active:scale-95"
              >
                {fc.ctaBooking} <ExternalLink className="h-5 w-5" />
              </a>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
